// Per-connection congestion brake for the mesh; see docs/repasse.md.

export interface CongestionState {
    /** Share of the bitrate ceiling allowed now (1 = full). */
    ratio: number
    healthyStreak: number
    badStreak: number
    /** Lowest RTT seen, in seconds; RTT above it is queueing on the link. */
    rttFloor: number
}

export interface CongestionSample {
    fractionLost: number
    /** Seconds. */
    rtt: number
    /** Encoder `qualityLimitationReason`. */
    limitation: string
}

export const MIN_RATIO = 0.45

export function initialCongestion(): CongestionState {
    return { ratio: 1, healthyStreak: 0, badStreak: 0, rttFloor: 0 }
}

/**
 * One step per stats read (~2 s): three bad reads take 10%, two clean ones give 25% back.
 *
 * Clean is about the link only (no loss, no queue), not the encoder's limitation: under a
 * lowered ceiling the encoder keeps reporting `bandwidth` (or `cpu` on a busy machine), so
 * waiting for `none` left a connection stuck at the floor for the rest of the session.
 */
export function stepCongestion(state: CongestionState, sample: CongestionSample): CongestionState {
    const rttFloor = sample.rtt <= 0
        ? state.rttFloor
        : state.rttFloor === 0 || sample.rtt < state.rttFloor
            ? sample.rtt
            : Math.min(sample.rtt, state.rttFloor * 1.02)
    const queue = rttFloor > 0 ? sample.rtt - rttFloor : 0
    const limited = sample.limitation === 'bandwidth' || sample.limitation === 'cpu'

    if (limited && (sample.fractionLost > 0.04 || queue > 0.15)) {
        const badStreak = state.badStreak + 1
        if (badStreak < 3) return { ...state, rttFloor, healthyStreak: 0, badStreak }
        return { rttFloor, healthyStreak: 0, badStreak: 0, ratio: Math.max(MIN_RATIO, state.ratio * 0.9) }
    }
    if (sample.fractionLost <= 0.01 && queue < 0.05) {
        const healthyStreak = state.healthyStreak + 1
        if (healthyStreak < 2 || state.ratio >= 1) return { ...state, rttFloor, badStreak: 0, healthyStreak }
        return { rttFloor, badStreak: 0, healthyStreak: 0, ratio: Math.min(1, state.ratio * 1.25) }
    }
    return { ...state, rttFloor, badStreak: 0 }
}

export interface ConnectionHealth extends CongestionSample {
    /** The selected candidate pair goes through TURN. */
    viaTurn: boolean
}

export function readHealth(report: RTCStatsReport): ConnectionHealth {
    let fractionLost = 0
    let rtt = 0
    let limitation = 'none'
    let selectedPair: string | undefined
    const pairs = new Map<string, { local?: string; remote?: string; nominated?: boolean; state?: string }>()
    const candidates = new Map<string, string>()

    report.forEach((stat) => {
        const item = stat as Record<string, unknown> & { type?: string; id?: string; kind?: string }
        if (item.type === 'remote-inbound-rtp' && item.kind === 'video') {
            fractionLost = Math.max(fractionLost, Number(item.fractionLost) || 0)
            rtt = Math.max(rtt, Number(item.roundTripTime) || 0)
        } else if (item.type === 'outbound-rtp' && item.kind === 'video') {
            const reason = typeof item.qualityLimitationReason === 'string' ? item.qualityLimitationReason : 'none'
            if (reason !== 'none' && limitation !== 'bandwidth') limitation = reason
        } else if (item.type === 'transport' && typeof item.selectedCandidatePairId === 'string') {
            selectedPair = item.selectedCandidatePairId
        } else if (item.type === 'candidate-pair' && item.id) {
            pairs.set(item.id, {
                local: item.localCandidateId as string | undefined,
                remote: item.remoteCandidateId as string | undefined,
                nominated: item.nominated as boolean | undefined,
                state: item.state as string | undefined
            })
        } else if ((item.type === 'local-candidate' || item.type === 'remote-candidate') && item.id) {
            candidates.set(item.id, String(item.candidateType ?? ''))
        }
    })

    // Firefox has no `transport` stat; fall back to the nominated pair.
    const pair = (selectedPair && pairs.get(selectedPair))
        || [...pairs.values()].find((candidate) => candidate.nominated && candidate.state === 'succeeded')
    const viaTurn = !!pair && (
        candidates.get(pair.local ?? '') === 'relay' || candidates.get(pair.remote ?? '') === 'relay'
    )
    return { fractionLost, rtt, limitation, viaTurn }
}
